import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Cake } from 'lucide-react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BirthdayPrompt({ open, onComplete }) {
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');
  const [loading, setLoading] = useState(false);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getDaysInMonth = (m) => {
    if (!m) return 31;
    return [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][parseInt(m) - 1];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!month || !day) return;
    setLoading(true);
    try {
      const birthday = `${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      await axios.post(`${API}/auth/set-birthday`, { birthday });
      toast.success('Birthday saved!');
      onComplete();
    } catch (error) {
      if (error.response?.status === 401) {
        // Token expired — the global interceptor will handle logout
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to save birthday. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="bg-[#18181B] border-[#27272A] max-w-sm"
        data-testid="birthday-prompt"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex justify-center mb-3">
            <div className="bg-[#CCFF00]/10 p-4 rounded-full">
              <Cake className="w-10 h-10 text-[#CCFF00]" />
            </div>
          </div>
          <DialogTitle className="text-2xl font-heading font-black uppercase text-[#FAFAFA] text-center">
            When's Your Birthday?
          </DialogTitle>
          <DialogDescription className="text-[#A1A1AA] text-center">
            We'd like to celebrate your special day with you!
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Month</Label>
              <select
                value={month}
                onChange={(e) => { setMonth(e.target.value); setDay(''); }}
                required
                data-testid="birthday-month"
                className="w-full bg-[#09090B] border border-[#27272A] text-white rounded-sm h-10 px-3 focus:border-[#CCFF00] focus:outline-none text-sm"
              >
                <option value="">Select</option>
                {months.map((m, i) => (
                  <option key={i} value={String(i + 1)}>{m}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Day</Label>
              <select
                value={day}
                onChange={(e) => setDay(e.target.value)}
                required
                data-testid="birthday-day"
                className="w-full bg-[#09090B] border border-[#27272A] text-white rounded-sm h-10 px-3 focus:border-[#CCFF00] focus:outline-none text-sm"
              >
                <option value="">Select</option>
                {Array.from({ length: getDaysInMonth(month) }, (_, i) => (
                  <option key={i + 1} value={String(i + 1)}>{i + 1}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={loading || !month || !day}
              data-testid="birthday-submit"
              className="w-full bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-[#B3E600] rounded-sm"
            >
              {loading ? 'Saving...' : 'Save Birthday'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
